import { useMemo } from "react";
import { Box, Flex, Heading, HStack, Stack, Text } from "@chakra-ui/react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  montarBarrasDeQuestao,
  montarBarrasDeTema,
  montarPerfil,
  montarPilhaDeTemas,
} from "./notaPorTema";
import { exibiveis } from "./filtrosDeCertificado";

// EG-38 — Dashboard "Resultados Gerais" (painel admin). Reusa o dado de
// `certificate/score`: nota por pilar vs. nota de corte (absoluto × corte).
//
// EG-55 — cada pilar passa a ser fatiado em seus temas. A fatia é COMPOSIÇÃO,
// nunca avaliação: tema tem nota e não tem corte (AD-2). O preparo do dado dos
// dois gráficos vive em notaPorTema.js, com rede própria — aqui fica só o
// render.

const INK = "#292B30";
const INK_MUTED = "#5C6066";
const BRAND = "#0073BB";
const CUTOFF = "#64645e";
const GOOD = "#2F855A";
const WARN = "#C05621";
const SURFACE = "#FFFFFF";

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

export function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  // EG-55: numa barra empilhada o recharts entrega TODAS as séries do gráfico,
  // inclusive as dos outros pilares — que nesta barra chegam sem valor. Listar
  // essas é anunciar temas que não pertencem ao pilar que o usuário apontou.
  const doPilar = payload.filter((p) => p.value !== undefined && p.value !== null);
  if (doPilar.length === 0) return null;

  // O corte não é um segmento da pilha: ele é a régua. Separá-lo permite pôr a
  // SOMA do pilar do lado dele, que é a comparação que o gráfico existe para
  // fazer (AD-1) — antes o usuário via 4 temas e tinha que somar de cabeça.
  const segmentos = doPilar.filter((p) => p.dataKey !== "corte");
  const corte = doPilar.find((p) => p.dataKey === "corte");

  // Com um segmento só (pilar sem tema, AD-5) o total seria o mesmo número
  // repetido — a linha do segmento já é a nota do pilar.
  const total = segmentos.length > 1 ? segmentos[0]?.payload?.nota : undefined;

  const linha = (chave, cor, nome, valor, forte) => (
    <HStack key={chave} spacing="8px">
      <Box w="10px" h="10px" borderRadius="2px" bg={cor} />
      <Text fontSize="12px" color={forte ? INK : INK_MUTED} fontWeight={forte ? 600 : 400}>
        {nome}: <b>{valor}</b>
      </Text>
    </HStack>
  );

  // Só as barras de tema e de questão trazem estes dois: o eixo mostra um rótulo
  // curto (o código da questão, por exemplo) e é aqui que cabe o resto — o
  // caminho de origem e o texto completo, que a legenda embaixo trunca.
  const { contexto, descricao } = segmentos[0]?.payload ?? {};

  return (
    <Box
      bg={SURFACE}
      border="1px solid #E2E8F0"
      borderRadius="8px"
      p="10px"
      boxShadow="sm"
      maxW="320px"
    >
      <Text fontSize="13px" fontWeight={600} color={INK} mb={contexto ? "2px" : "4px"}>
        {label}
      </Text>
      {contexto && (
        <Text fontSize="11px" color={INK_MUTED} mb="4px">
          {contexto}
        </Text>
      )}
      {descricao && (
        <Text fontSize="12px" color={INK} mb="6px" lineHeight="1.35">
          {descricao}
        </Text>
      )}
      {segmentos.map((p) => linha(p.name, p.color, p.name, p.value, false))}
      {(total !== undefined || corte) && (
        <Box
          mt="6px"
          pt="6px"
          borderTop={segmentos.length > 0 ? "1px solid #EDF2F7" : "none"}
        >
          {total !== undefined && linha("total", INK, "Total do pilar", total, true)}
          {corte && linha("corte", corte.color, corte.name, corte.value, false)}
        </Box>
      )}
    </Box>
  );
}

function StatusBadge({ certified }) {
  return (
    <HStack
      spacing="6px"
      bg={certified ? "#E6F4EA" : "#FDECEC"}
      color={certified ? GOOD : WARN}
      px="10px"
      py="4px"
      borderRadius="full"
    >
      <Text fontSize="13px" fontWeight={600}>
        {certified ? "Certificado" : "Não certificado"}
      </Text>
    </HStack>
  );
}

/**
 * Moldura de um gráfico: título + região nomeada.
 *
 * O role="group" com aria-label não é enfeite de acessibilidade — é o que
 * permite prender por teste a ORDEM dos quatro gráficos, que o TL pediu
 * explicitamente. O conteúdo deles vive dentro do ResponsiveContainer e não
 * chega ao DOM em jsdom.
 */
function Grafico({ titulo, children }) {
  return (
    <Box role="group" aria-label={titulo}>
      <Text fontSize="14px" fontWeight={600} color={INK} mb="12px">
        {titulo}
      </Text>
      {children}
    </Box>
  );
}

/**
 * Barras existem mas todas valem zero — o gráfico desenha e não se vê nada.
 *
 * É o estado dominante hoje: 4 de 13680 respostas têm nota. Sem distinguir isso
 * de "não classificado", a tela vazia é ambígua e leva a caçar bug de
 * classificação quando o que falta é avaliação do revisor.
 */
const semNota = (barras) => barras.every((b) => b.nota === 0);

function Vazio({ children }) {
  return (
    <Flex h="120px" align="center" justify="center" textAlign="center" px="24px">
      <Text fontSize="13px" color={INK_MUTED} maxW="420px">
        {children}
      </Text>
    </Flex>
  );
}

/**
 * Barras de uma dimensão sem corte (tema ou questão): série única, uma cor por
 * barra, herdada do pilar de origem.
 *
 * `interval={0}` força TODOS os rótulos a aparecer — o padrão do recharts é
 * pular rótulos quando não cabem, e pular rótulo aqui esconde questão. O
 * ângulo é o que faz caber: o pior caso medido na base é 38 barras.
 */
function BarrasSimples({ dados, altura = 260 }) {
  return (
    <ResponsiveContainer width="100%" height={altura}>
      {/* margin.bottom fica em 0: a altura do XAxis já é o espaço dos rótulos
          inclinados, e o recharts SOMA os dois. Com bottom 64 + height 64 sobrava
          um vão de ~128px entre o eixo e o que vem depois. */}
      <ComposedChart data={dados} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
        <XAxis
          dataKey="rotulo"
          tick={{ fontSize: 10, fill: INK_MUTED }}
          tickLine={false}
          axisLine={{ stroke: "#E2E8F0" }}
          interval={0}
          angle={-40}
          textAnchor="end"
          height={64}
        />
        <YAxis tick={{ fontSize: 12, fill: INK_MUTED }} tickLine={false} axisLine={false} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,115,187,0.06)" }} />
        <Bar dataKey="nota" name="Nota" radius={[4, 4, 0, 0]} maxBarSize={48}>
          {dados.map((d) => (
            <Cell key={`${d.id_pillar}-${d.rotulo}`} fill={d.cor} />
          ))}
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * Legenda "código — descrição" sob o gráfico por questão.
 *
 * O eixo mostra só o código, que não diz o que a questão pergunta. A descrição
 * tem média de 133 caracteres e chega a 1961 na base: não cabe em eixo, e uma
 * lista sem truncar viraria parede de texto. Aqui ela vem em UMA linha por item
 * (o resto fica no tooltip, que é onde texto longo cabe sem quebrar layout).
 *
 * Questão sem descrição não entra — o backend faz LEFT JOIN e a descrição não é
 * garantida; uma linha com código e nada ao lado seria pior que a ausência.
 */
function LegendaDeQuestoes({ dados }) {
  const comDescricao = dados.filter((d) => d.descricao);
  if (comDescricao.length === 0) return null;

  return (
    <Box
      as="ul"
      role="list"
      aria-label="Descrição das questões"
      mt="12px"
      pt="12px"
      borderTop="1px solid #EDF2F7"
      listStyleType="none"
      sx={{ columnCount: [1, 1, 2], columnGap: "24px" }}
    >
      {comDescricao.map((d) => (
        <Box as="li" key={`${d.id_pillar}-${d.rotulo}`} mb="4px">
          <Text fontSize="12px" color={INK_MUTED} noOfLines={1}>
            <Box as="span" display="inline-block" w="8px" h="8px" borderRadius="2px" bg={d.cor} mr="6px" />
            <b>{d.rotulo}</b> — {d.descricao}
          </Text>
        </Box>
      ))}
    </Box>
  );
}

function CertificateSection({ certificate }) {
  const { rows, series } = useMemo(
    () => montarPilhaDeTemas(certificate.pillars),
    [certificate.pillars]
  );

  const { eixos, fragmentado } = useMemo(
    () => montarPerfil(certificate.pillars),
    [certificate.pillars]
  );

  const barrasDeTema = useMemo(
    () => montarBarrasDeTema(certificate.pillars),
    [certificate.pillars]
  );

  const barrasDeQuestao = useMemo(
    () => montarBarrasDeQuestao(certificate.pillars),
    [certificate.pillars]
  );

  const overallScore = useMemo(
    () =>
      certificate.score != null
        ? Number(certificate.score)
        : rows.reduce((acc, r) => acc + r.nota, 0),
    [certificate.score, rows]
  );

  // A soma dos cortes dos pilares: dá escala à nota total, que sozinha não diz
  // se 218 é muito ou pouco.
  const corteTotal = useMemo(
    () => rows.reduce((acc, r) => acc + r.corte, 0),
    [rows]
  );

  const failing = rows.filter((r) => !r.passed);
  const validUntil = formatDate(certificate.valid_until);

  if (rows.length === 0) return null;

  return (
    <Box
      as="section"
      role="region"
      aria-label={certificate.name}
      bg={SURFACE}
      borderRadius="12px"
      p={["20px", "28px"]}
      mb="24px"
    >
      <Flex justify="space-between" align="flex-start" gap="16px" flexWrap="wrap" mb="20px">
        <Heading size="md" color={INK} lineHeight="1.2">
          {certificate.name}
        </Heading>
        <Stack spacing="8px" align="flex-end">
          <StatusBadge certified={Boolean(certificate.certified)} />
          {validUntil && (
            <Text fontSize="13px" color={INK_MUTED}>
              Válido até {validUntil}
            </Text>
          )}
        </Stack>
      </Flex>

      <HStack spacing="24px" mb="24px" flexWrap="wrap">
        <Box>
          <Text fontSize="12px" color={INK_MUTED}>
            Nota total
          </Text>
          <HStack align="baseline" spacing="6px">
            <Text fontSize="28px" fontWeight={700} color={BRAND} lineHeight="1.1">
              {overallScore.toFixed(2)}
            </Text>
            {/* Referência de escala, NÃO critério de aprovação: certificar exige
                que CADA pilar passe o próprio corte (a guarda every()), não que a
                soma passe. Quem dá o veredito é o rodapé de pilares abaixo do
                corte. */}
            <Text fontSize="16px" color={INK_MUTED}>
              / {corteTotal.toFixed(2)}
            </Text>
          </HStack>
        </Box>
      </HStack>

      {/* Ordem de prioridade pedida pelo TL: do mais agregado ao mais fino, com
          o radar por último. Empilhados em vez de lado a lado — os três de
          barras precisam de largura, e o gráfico por questão pode chegar a 38
          barras (pior caso medido na base). */}
      <Stack spacing="28px">
        <Grafico titulo="Nota por pilar × corte">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
              <XAxis
                dataKey="pillar"
                tick={{ fontSize: 12, fill: INK_MUTED }}
                tickLine={false}
                axisLine={{ stroke: "#E2E8F0" }}
              />
              <YAxis tick={{ fontSize: 12, fill: INK_MUTED }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,115,187,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {/* AD-1: uma <Bar> por tema, todas no mesmo stackId — o TOPO da
                  pilha continua sendo a nota do pilar, que é o que a linha de
                  corte compara. Pilar sem tema tem uma série só, com o nome do
                  pilar, e a barra fica igual à de hoje (AD-5). */}
              {series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.nome}
                  stackId="nota"
                  fill={s.cor}
                  radius={fragmentado ? 0 : [4, 4, 0, 0]}
                  maxBarSize={48}
                />
              ))}
              {/* AD-2: o corte é do pilar e incide sobre a SOMA. Esta linha não
                  se fragmenta — não existe corte por tema. */}
              <Line
                dataKey="corte"
                name="Corte"
                stroke={CUTOFF}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={{ r: 3, fill: CUTOFF }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Grafico>

        {/* Barras por tema: aqui cada tema tem barra PRÓPRIA, para comparar
            temas entre si — diferente do gráfico 1, onde o tema é fatia dentro
            da barra do pilar. Sem corte: ele é do pilar (AD-2). */}
        <Grafico titulo="Nota por tema">
          {/* Três estados, não dois. Sem esta separação, "falta classificar" e
              "falta o revisor avaliar" produzem a MESMA tela vazia — e a
              conclusão natural de quem olha é que a classificação não funcionou.
              São problemas diferentes, com donos diferentes. */}
          {barrasDeTema.length === 0 ? (
            <Vazio>
              Nenhuma questão deste certificado está classificada por tema ainda
              — a nota por tema aparece assim que a classificação for feita.
            </Vazio>
          ) : semNota(barrasDeTema) ? (
            <Vazio>
              As questões estão classificadas por tema, mas nenhuma questão
              avaliada ainda — as barras aparecem quando o revisor lançar as
              notas.
            </Vazio>
          ) : (
            <BarrasSimples dados={barrasDeTema} />
          )}
        </Grafico>

        {/* Barras por questão: a altura mais fina da nota. As questões ficam
            agrupadas por pilar, sem intercalar — pedido explícito do TL. */}
        <Grafico titulo="Nota por questão">
          {barrasDeQuestao.length > 0 ? (
            <>
              <BarrasSimples dados={barrasDeQuestao} altura={300} />
              <Text fontSize="12px" color={INK_MUTED}>
                {barrasDeQuestao.length} questões, agrupadas por pilar
              </Text>
              <LegendaDeQuestoes dados={barrasDeQuestao} />
            </>
          ) : (
            <Vazio>Sem questões avaliadas neste certificado.</Vazio>
          )}
        </Grafico>

        {/* AD-7/AD-8: o TL escolheu o radar. O treemap e o segmented control que
            servia para comparar os dois foram apagados na mesma entrega — toggle
            de decisão que sobrevive ao merge vira configuração permanente. */}
        <Grafico titulo={fragmentado ? "Perfil por tema" : "Perfil por pilar"}>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={eixos} outerRadius="72%">
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="eixo" tick={{ fontSize: 11, fill: INK_MUTED }} />
              {/* AD-2: com eixo de TEMA o polígono de corte compararia parte
                  com total, então ele não existe. Com eixo de pilar — o caso
                  de todos os certificados de produção hoje — o corte continua
                  significando o que sempre significou. */}
              {!fragmentado && (
                <Radar
                  name="Corte"
                  dataKey="corte"
                  stroke={CUTOFF}
                  fill={CUTOFF}
                  fillOpacity={0.08}
                />
              )}
              <Radar name="Nota" dataKey="nota" stroke={BRAND} fill={BRAND} fillOpacity={0.28} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </Grafico>
      </Stack>

      {/* AD-2: este rodapé narra APROVAÇÃO, e aprovação não existe por tema —
          o corte é do pilar. Fragmentar esta lista seria afirmar reprovação de
          algo que nunca foi avaliado. */}
      <Box
        mt="20px"
        pt="16px"
        borderTop="1px solid #EDF2F7"
        role="note"
        aria-label={
          failing.length > 0
            ? "Pilares abaixo do corte"
            : "Todos os pilares atingiram o corte"
        }
      >
        {failing.length > 0 ? (
          <>
            <Text fontSize="13px" fontWeight={600} color={WARN} mb="8px">
              Pilares abaixo do corte
            </Text>
            <Flex gap="8px" flexWrap="wrap">
              {failing.map((r) => (
                <Text
                  key={r.pillar}
                  fontSize="12px"
                  bg="#FDECEC"
                  color={WARN}
                  px="10px"
                  py="4px"
                  borderRadius="6px"
                >
                  {r.pillar} — {r.nota} / {r.corte}
                </Text>
              ))}
            </Flex>
          </>
        ) : (
          <Text fontSize="13px" color={GOOD}>
            Todos os pilares atingiram o corte
          </Text>
        )}
      </Box>
    </Box>
  );
}

export default function CertificationDashboard({ certificates }) {
  const withPillars = exibiveis(certificates);
  // Certificado de "só nota": o questionário tem has_pillar = false, o backend
  // não monta `pillars` e os dois gráficos não existem para ele. Esconder está
  // certo — esconder sem dizer deixa quem olha a tela sem explicação para a
  // diferença entre o que a empresa tem vinculado e o que aparece aqui.
  const ocultos = (certificates || []).filter(
    (c) => (c.pillars?.length ?? 0) === 0
  );

  if (withPillars.length === 0) {
    return (
      <Flex
        bg={SURFACE}
        borderRadius="12px"
        p={["32px", "48px"]}
        direction="column"
        align="center"
        textAlign="center"
        gap="8px"
      >
        <Heading size="sm" color={INK}>
          Nenhum gráfico disponível ainda
        </Heading>
        <Text color={INK_MUTED} fontSize="14px" maxW="420px">
          Assim que as certificações desta empresa tiverem pilares avaliados, os gráficos de
          desempenho aparecem aqui.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>
      {withPillars.map((certificate) => (
        <CertificateSection key={certificate.id_certificate} certificate={certificate} />
      ))}

      {ocultos.length > 0 && (
        <Box
          role="note"
          aria-label="Certificações que não aparecem aqui"
          bg="#F7F9FC"
          borderRadius="8px"
          p="14px 16px"
        >
          <Text fontSize="13px" color={INK_MUTED}>
            <b>
              {ocultos.length === 1
                ? "1 certificação não aparece"
                : `${ocultos.length} certificações não aparecem`}
            </b>{" "}
            nesta visão: {ocultos.map((c) => c.name).join(", ")}. Elas são
            avaliadas por nota única, sem pilar — e os dois gráficos comparam
            nota de pilar com a nota de corte do pilar.
          </Text>
        </Box>
      )}
    </Box>
  );
}
